import { useTranslation } from "@/hooks/useTranslation";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Checkbox,
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
} from "@st-marketplace/ui";
import type React from "react";
import { useForm } from "react-hook-form";
import {
	type RelationFormData,
	type RelationInitialValues,
	getRelationSchema,
} from "./relationsForm.schema";

type RelationFormProps = {
	initialValues: RelationInitialValues;
	loading?: boolean;
	disabledFields?: Partial<Record<keyof RelationInitialValues, boolean>>;
	onSubmit: (data: RelationFormData) => Promise<void> | void;
	onReset?: () => void;
};

export const RelationForm: React.FC<RelationFormProps> = ({
	initialValues,
	loading = false,
	disabledFields,
	onSubmit,
	onReset,
}) => {
	const { t } = useTranslation();
	const schema = getRelationSchema(t);

	const form = useForm<RelationFormData>({
		resolver: zodResolver(schema),
		defaultValues: {
			maxDebt: initialValues.maxDebt,
			useInheritedPrice: initialValues.useInheritedPrice,
			localPrice: initialValues.localPrice,
			isAllowToOrder: initialValues.isAllowToOrder,
		},
	});

	const handleReset = () => {
		form.reset();
		onReset?.();
	};

	const handleSubmit = form.handleSubmit(async (data) => {
		await onSubmit(data);
		handleReset();
	});
	const useInheritedPrice = form.watch("useInheritedPrice");

	return (
		<Form {...form}>
			<form
				noValidate
				onSubmit={handleSubmit}
				className="flex flex-col gap-4 p-1"
			>
				<FormField
					control={form.control}
					name="maxDebt"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t("relations.edit.inputMaxDebt")}</FormLabel>
							<FormControl>
								<Input
									type="number"
									placeholder={t("relations.edit.inputMaxDebt")}
									value={field.value ?? ""}
									onChange={field.onChange}
									disabled={!!disabledFields?.maxDebt}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<div>
					<Checkbox
						label={t("relations.edit.checkboxInheritGlobalPrice")}
						checked={useInheritedPrice}
						disabled={loading || !!disabledFields?.localPrice}
						onCheckedChange={(checked) => {
							const next = checked === true;
							form.setValue("useInheritedPrice", next);
							if (next) {
								form.setValue("localPrice", null);
								form.clearErrors("localPrice");
							}
						}}
					/>
					<FormDescription>
						{t("relations.edit.inheritGlobalPriceHelper")}
					</FormDescription>

					{!useInheritedPrice && (
						<FormField
							control={form.control}
							name="localPrice"
							render={({ field }) => (
								<FormItem className="mt-4">
									<FormLabel required>
										{t("relations.edit.inputLocalPrice")}
									</FormLabel>
									<FormControl>
										<Input
											type="number"
											placeholder={t("relations.edit.inputLocalPrice")}
											value={field.value ?? ""}
											onChange={field.onChange}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}
				</div>

				<FormField
					control={form.control}
					name="isAllowToOrder"
					render={({ field }) => (
						<FormItem>
							<Checkbox
								label={t("relations.edit.checkboxAllowToOrder")}
								checked={field.value ?? false}
								disabled={!!disabledFields?.isAllowToOrder}
								onCheckedChange={field.onChange}
							/>
						</FormItem>
					)}
				/>

				<div className="grid gap-4 grid-cols-2">
					<Button
						type="button"
						color="secondary"
						className="col-span-1"
						onClick={handleReset}
					>
						{t("common.button.reset")}
					</Button>

					<Button
						type="submit"
						color="primary"
						className="col-span-1"
						loading={loading}
					>
						{t("relations.edit.buttonSubmit")}
					</Button>
				</div>
			</form>
		</Form>
	);
};
